import { Route } from '@angular/router';
import { AuthGuard, RoleGuard } from '@marina/auth';
import { defaultHomeRedirectGuard } from './guards/default-home-redirect.guard';

export const appRoutes: Route[] = [
  {
    path: '',
    pathMatch: 'full',
    canActivate: [AuthGuard, defaultHomeRedirectGuard],
    loadComponent: () =>
      import('./core/empty-page.component').then((m) => m.EmptyPageComponent),
  },
  {
    path: 'unauthorized',
    loadComponent: () =>
      import('./features/unauthorized/unauthorized.component').then(
        (m) => m.UnauthorizedComponent
      ),
    // Intentionally unguarded: guards/interceptors may redirect here on 403/role failures
  },
  {
    path: 'auth',
    loadChildren: () =>
      import('./features/auth/auth.module').then((m) => m.AuthModule),
  },
  {
    path: 'dashboard',
    loadChildren: () =>
      import('./features/dashboard/dashboard.module').then(
        (m) => m.DashboardModule
      ),
    canActivate: [AuthGuard, RoleGuard],
    data: { roles: ['ADMIN', 'DOCK_MASTER', 'OFFICE_ADMIN', 'SERVICE_MANAGER', 'SERVICE_TECH'] },
  },
  {
    path: 'marina-settings',
    loadChildren: () =>
      import('./features/marina-settings/marina-settings.module').then(
        (m) => m.MarinaSettingsModule
      ),
    canActivate: [AuthGuard, RoleGuard],
    data: {
      roles: ['ADMIN', 'DOCK_MASTER', 'OFFICE_ADMIN'],
    },
  },
  {
    path: 'system-settings',
    loadChildren: () =>
      import('./features/system-settings/system-settings.module').then(
        (m) => m.SystemSettingsModule
      ),
    canActivate: [AuthGuard], // System admin check is done in the component/service
  },
  {
    path: 'dry-storage',
    loadComponent: () =>
      import('./features/dry-storage/rack-management/rack-management.component').then(
        (m) => m.RackManagementComponent
      ),
    canActivate: [AuthGuard, RoleGuard],
    data: { roles: ['ADMIN', 'DOCK_MASTER'] },
  },
  {
    path: 'fleet-management',
    loadChildren: () =>
      import('./features/fleet-management/fleet-management.module').then(
        (m) => m.FleetManagementModule
      ),
    canActivate: [AuthGuard, RoleGuard],
    data: { roles: ['ADMIN', 'DOCK_MASTER', 'OFFICE_ADMIN', 'SERVICE_MANAGER'] },
  },
  {
    path: 'vessels',
    loadChildren: () =>
      import('./features/vessels/vessels.module').then(
        (m) => m.VesselsModule
      ),
    canActivate: [AuthGuard, RoleGuard],
    data: { roles: ['ADMIN', 'DOCK_MASTER', 'OFFICE_ADMIN', 'SERVICE_MANAGER'] },
  },
  {
    path: 'clients',
    loadChildren: () =>
      import('./features/clients/clients.module').then(
        (m) => m.ClientsModule
      ),
    canActivate: [AuthGuard, RoleGuard],
    data: { roles: ['ADMIN', 'DOCK_MASTER', 'OFFICE_ADMIN', 'SERVICE_MANAGER'] },
  },
  {
    path: 'lease-management',
    loadChildren: () =>
      import('./features/lease-management/lease-management.module').then(
        (m) => m.LeaseManagementModule
      ),
    canActivate: [AuthGuard, RoleGuard],
    data: {
      roles: ['ADMIN', 'DOCK_MASTER', 'OFFICE_ADMIN'],
    },
  },
  {
    path: 'billing',
    loadChildren: () =>
      import('./features/billing/billing.module').then(
        (m) => m.BillingModule
      ),
    canActivate: [AuthGuard, RoleGuard],
    data: { roles: ['ADMIN', 'DOCK_MASTER', 'OFFICE_ADMIN', 'SERVICE_MANAGER'] },
  },
  {
    path: 'reports',
    loadChildren: () =>
      import('./features/reports/reports.module').then((m) => m.ReportsModule),
    canActivate: [AuthGuard, RoleGuard],
    data: { roles: ['ADMIN', 'DOCK_MASTER', 'OFFICE_ADMIN', 'SERVICE_MANAGER'] },
  },
  {
    path: 'pos',
    loadChildren: () =>
      import('./features/pos/pos.module').then(
        (m) => m.PosModule
      ),
    canActivate: [AuthGuard, RoleGuard],
    data: { roles: ['ADMIN', 'DOCK_MASTER', 'OFFICE_ADMIN', 'SERVICE_MANAGER', 'DECK_HAND'] },
  },
  {
    path: 'docusign/return',
    loadComponent: () =>
      import('./features/lease-management/docusign-return/docusign-return.component').then(
        (m) => m.DocusignReturnComponent
      ),
    canActivate: [AuthGuard],
  },
  {
    path: 'audit-logs',
    loadChildren: () =>
      import('./features/audit-logs/audit-logs.module').then(
        (m) => m.AuditLogsModule
      ),
    canActivate: [AuthGuard, RoleGuard],
    data: { roles: ['ADMIN', 'DOCK_MASTER'] },
  },
  {
    path: 'fuel-management',
    loadChildren: () =>
      import('./features/fuel-management/fuel-management.module').then(
        (m) => m.FuelManagementModule
      ),
    canActivate: [AuthGuard, RoleGuard],
    data: { roles: ['ADMIN', 'DOCK_MASTER'] },
  },
  {
    path: 'inventory',
    loadChildren: () =>
      import('./features/inventory/inventory.module').then(
        (m) => m.InventoryModule
      ),
    canActivate: [AuthGuard, RoleGuard],
    data: {
      roles: [
        'ADMIN',
        'DOCK_MASTER',
        'OFFICE_ADMIN',
        'SERVICE_MANAGER',
        'SERVICE_TECH',
      ],
    },
  },
  {
    path: 'launch-board',
    loadChildren: () =>
      import('./features/launch-board/launch-board.module').then(
        (m) => m.LaunchBoardModule
      ),
    canActivate: [AuthGuard, RoleGuard],
    data: { roles: ['ADMIN', 'DOCK_MASTER', 'DECK_HAND'] },
  },
  {
    path: 'account-security',
    loadChildren: () =>
      import('./features/account-security/account-security.module').then(
        (m) => m.AccountSecurityModule
      ),
    canActivate: [AuthGuard],
  },
  // Legacy routes - redirect to account-security
  {
    path: 'devices',
    redirectTo: '/account-security',
    pathMatch: 'full',
  },
  {
    path: 'sessions',
    redirectTo: '/account-security',
    pathMatch: 'full',
  },
  // Send unknown paths to `/` so defaultHomeRedirectGuard picks role-appropriate home (e.g. deck hand → launch board).
  {
    path: '**',
    redirectTo: '/',
  },
];
